/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import { EuiLink, EuiCode, EuiCodeBlock, EuiSpacer, EuiAccordion, EuiCallOut } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

interface MutationError {
  error?: string
  message?: string
  detail?: {
    link?: string
  }
}

/* ==========================================================================
   COMPONENT DEFINITION
   ========================================================================== */

export const DomainChangeErrorCallOut = ({ error }: { error: any }) => {
  if (!error) {
    return null
  }

  const err = error as MutationError

  return (
    <EuiCallOut
      title={
        <strong>
          <FormattedMessage id='change-domain-flyout.error' defaultMessage='Failed Conversion' />
        </strong>
      }
      color='danger'
      iconType='alert'
      data-test-id='fail-callout'
    >
      <EuiSpacer size='m' />
      {/* SECTION 1: THE ERROR CODE */}
      {err.error && (
        <p>
          <FormattedMessage id='change-domain-flyout.error.code' defaultMessage='Code:' />
          <EuiCode transparentBackground={true} style={{ fontWeight: 'bold' }}>
            {err.error}
          </EuiCode>
        </p>
      )}

      {/* SECTION 2: THE RAW MESSAGE */}
      {err?.message}

      {/* SECTION 3: THE GUIDANCE LINK */}
      <EuiSpacer size='m' />
      {err.detail?.link && (
        <p>
          <EuiLink href={err.detail.link} target='_blank' external={true}>
            <FormattedMessage
              id='change-domain-flyout.errorLink'
              defaultMessage='View guidance for this error'
            />
          </EuiLink>
        </p>
      )}

      {/* SECTION 4: FULL TECHNICAL DUMP */}
      <EuiAccordion id='errorDetails' buttonContent='View full technical details' paddingSize='s'>
        <EuiCodeBlock language='json' whiteSpace='pre-wrap' paddingSize='s'>
          {JSON.stringify(err, null, 2)}
        </EuiCodeBlock>
      </EuiAccordion>
    </EuiCallOut>
  )
}
